<template>
  <div v-if="showPaginationRef" class="flex items-center justify-between">
    <div class="text-main">
      <span v-if="props.showRange && props.page">{{ paginationRange }}&nbsp;</span>
      <span v-if="props.showTotal">{{ paginationTotalInfo }}</span>
    </div>
    <nav v-if="pageCount > 1" data-testid="pagination-nav">
      <ul class="list-style-none flex space-x-8">
        <li class="arrow">
          <button :disabled="onFirstPage" data-testid="pagination-prev" @click.stop="decrement">
            <SvgIcon :path="mdiChevronLeft" :size="20" />
          </button>
        </li>
        <template v-if="props.page">
          <li :class="{ 'txt-body-s-bold text-primary': props.page == 1 }">
            <button data-testid="pagination-page-1" @click.stop="setPage(1)">1</button>
          </li>
          <li v-if="pageCount >= 3" :class="{ 'txt-body-s-bold text-primary': props.page == 2 }">
            <button @click.stop="setPage(2)">2</button>
          </li>
          <li v-if="pageCount >= 4" :class="{ 'txt-body-s-bold text-primary': props.page == 3 }">
            <button @click.stop="setPage(3)">3</button>
          </li>
          <li v-if="pageCount > 3 && props.page > 4">
            <button disabled>…</button>
          </li>
          <li v-if="props.page > 3 && props.page != pageCount" class="text-primary">
            <button>{{ props.page }}</button>
          </li>
          <li v-if="pageCount >= 5 && props.page <= pageCount - 2">
            <button disabled>…</button>
          </li>
          <li :class="{ 'text-primary': props.page == pageCount }">
            <button data-testid="pagination-page-last" @click.stop="setPage(pageCount)">
              {{ pageCount }}
            </button>
          </li>
        </template>
        <li class="arrow">
          <button :disabled="onlastPage" data-testid="pagination-next" @click.stop="increment">
            <SvgIcon :path="mdiChevronRight" :size="20" />
          </button>
        </li>
      </ul>
    </nav>

    <div>
      <span v-if="showItemsPerPage" class="mr-4 text-main">{{ t('Pagination.itemsPerPage') }}:</span>
      <DropDownSelect v-if="showItemsPerPage" position="bottom" direction="right">
        <template #activator="{ open }">
          <button
            class="flex items-center rounded-sm border border-main p-2"
            data-testid="pagination-per-page-toggle"
            @click.prevent="open"
          >
            <span class="text-main">{{ perPageLabel }}</span>
            <SvgIcon :path="mdiChevronUp" :size="20" class="ml-3 text-main" />
          </button>
        </template>

        <template #default="{ close }">
          <ul class="m-0 list-none p-0">
            <li v-for="option in perPageOptions" :key="`${option.label}-items-per-page`" class="separate-rows">
              <label class="block cursor-pointer">
                <input v-model="perPage" type="radio" :value="option.value" class="peer pointer-events-none absolute opacity-0" />
                <span
                  class="block min-w-20 px-3 py-2 text-center peer-checked:bg-surface-elevation-2 hover:bg-surface-elevation-2"
                  @click="close"
                >
                  {{ option.label }}
                </span>
              </label>
            </li>
          </ul>
        </template>
      </DropDownSelect>
    </div>
  </div>
</template>

<script lang="ts">
export default { name: 'PaginationControl' }
export const defaultPerPage = 10

export type PaginationQueryVariables = {
  first: number | undefined
  after: string | undefined
  last: number | undefined
  before: string | undefined
}
</script>

<script setup lang="ts">
import { mdiChevronLeft, mdiChevronRight, mdiChevronUp } from '@mdi/js'
import { computed, type WritableComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import type { PageInfo } from '@/gql/myomr'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    page?: number
    pageInfo?: PageInfo
    perPage?: number
    showItemsPerPage?: boolean
    showPagination?: 'auto' | 'always'
    showRange?: boolean
    showTotal?: boolean
    total: number
    perPageOptions?: number[]
  }>(),
  {
    page: undefined,
    pageInfo: undefined,
    perPage: 10,
    perPageOptions: undefined,
    showItemsPerPage: true,
    showPagination: 'auto',
    showRange: true,
    showTotal: true,
    size: 8,
  },
)

const perPage: WritableComputedRef<number> = computed({
  get() {
    return props.perPage
  },
  set(newValue: number) {
    emit('update:perPage', newValue)
  },
})

const basePerPageOptions = [
  { label: '10', value: 10 },
  { label: '20', value: 20 },
  { label: '50', value: 50 },
]
const perPageOptions = computed(() => {
  if (props.perPageOptions) return props.perPageOptions.map((value) => ({ label: String(value), value }))
  if (props.total <= 1000) {
    return [...basePerPageOptions, { label: t('Pagination.showAll'), value: props.total }]
  } else {
    return basePerPageOptions
  }
})

const onFirstPage = computed(() => {
  if (props.page) {
    return props.page == 1
  } else {
    return !props.pageInfo?.hasPreviousPage
  }
})
const onlastPage = computed(() => {
  if (props.page) {
    return props.page == pageCount.value
  } else {
    return !props.pageInfo?.hasNextPage
  }
})

const perPageLabel = computed(() => perPageOptions.value.find((option) => option.value == perPage.value)?.label || defaultPerPage)

const pageCount = computed(() => {
  return Math.ceil(props.total / props.perPage)
})

const showPaginationRef = computed(() => {
  if (props.showPagination == 'always') return true
  return props.perPage ? props.total > props.perPage : props.total > defaultPerPage
})

const paginationTotalInfo = computed(() => {
  if (props.showRange && props.page) {
    return t('Pagination.total', { count: props.total })
  } else {
    return t('Pagination.totalOnly', props.total)
  }
})
const paginationRange = computed(() => {
  if (!props.page) return ''
  const from = (props.page - 1) * props.perPage + 1
  const to = Math.min(props.page * props.perPage, props.total)
  return `${from} - ${to}`
})

const emit = defineEmits<{
  (e: 'update:page', value: number): void
  (e: 'update:paginationQueryVariables', value: PaginationQueryVariables): void
  (e: 'update:perPage', value: number): void
}>()

function increment() {
  if (props.page) {
    emit('update:page', props.page + 1)
  } else if (props.pageInfo?.hasNextPage && props.pageInfo?.endCursor) {
    emit('update:paginationQueryVariables', {
      after: props.pageInfo.endCursor,
      before: undefined,
      first: perPage.value,
      last: undefined,
    })
  }
}
function decrement() {
  if (props.page) {
    emit('update:page', props.page + -1)
  } else if (props.pageInfo?.hasPreviousPage && props.pageInfo?.startCursor) {
    emit('update:paginationQueryVariables', {
      after: undefined,
      before: props.pageInfo.startCursor,
      first: undefined,
      last: perPage.value,
    })
  }
}
function setPage(page: number) {
  emit('update:page', page)
}
</script>

<style scoped>
@reference '@ramp106/omrjs-config-tailwind/styles/index.css';

button:disabled {
  @apply text-subtle;
}
</style>
